/**
 * 裁切体工厂函数
 * 快捷创建 Box / Polygon 类型的 ClipVolume
 */

import * as THREE from 'three'
import type { ClipVolume, ClipVolumeSpace } from '@/shared/types/core'

/**
 * 创建盒体裁切体。
 *
 * @param position - 裁切盒中心的世界坐标
 * @param scale - 裁切盒沿各轴的半尺寸缩放
 * @param rotation - 裁切盒旋转，缺省时为无旋转
 * @returns type 为 'box' 的 ClipVolume 描述
 */
export function createBoxVolume(
  position: THREE.Vector3,
  scale: THREE.Vector3,
  rotation?: THREE.Euler
): ClipVolume {
  const matrix = new THREE.Matrix4()
  matrix.compose(
    position,
    new THREE.Quaternion().setFromEuler(rotation ?? new THREE.Euler()),
    scale
  )
  return { type: 'box', matrix }
}

/**
 * 创建多边形裁切体，中心取各顶点几何平均。
 *
 * @param vertices - 多边形顶点世界坐标序列，空数组时中心退化为原点
 * @param options - 可选坐标空间、视图与投影矩阵；同时提供矩阵时默认使用 screen
 * @returns type 为 'polygon' 的 ClipVolume 描述
 */
export function createPolygonVolume(
  vertices: THREE.Vector3[],
  options?: {
    space?: ClipVolumeSpace
    viewMatrix?: THREE.Matrix4
    projMatrix?: THREE.Matrix4
  }
): ClipVolume {
  const center = new THREE.Vector3()
  for (const v of vertices) {
    center.add(v)
  }
  if (vertices.length > 0) {
    center.divideScalar(vertices.length)
  }

  const matrix = new THREE.Matrix4().setPosition(center)
  const inferredSpace =
    options?.viewMatrix && options.projMatrix ? ('screen' as const) : ('world' as const)

  return {
    type: 'polygon',
    matrix,
    vertices: vertices.map((v) => v.clone()),
    space: options?.space ?? inferredSpace,
    viewMatrix: options?.viewMatrix,
    projMatrix: options?.projMatrix,
  }
}
